import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Landmark,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { defaultBillingTiers } from "./billingPlans";
import "./LandingPage.css";
import "./LoginPage.css";

export default function RenewMembership() {
  const [searchParams] = useSearchParams();
  const tiers = defaultBillingTiers;
  const [cardroom, setCardroom] = useState(searchParams.get("cardroom") || "");
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("pro");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const renewalStatus = searchParams.get("renewal");

  const selectedTier = useMemo(
    () => tiers.find((item) => item.id === tier) || tiers[2],
    [tier],
  );
  const selectedPrice =
    billingCycle === "annual"
      ? selectedTier.annualPrice
      : selectedTier.monthlyPrice;
  const allowsCard = tier === "basic" && billingCycle === "monthly";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("");
    setLoading(true);

    try {
      const response = await fetch("/api/stripe/create-renewal-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cardroom: cardroom.trim(),
          email: email.trim(),
          tier,
          billingCycle,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Could not start renewal checkout.");
      }

      window.location.assign(data.url);
    } catch (err) {
      setStatus(err.message);
      setLoading(false);
    }
  };

  return (
    <main className="nexus-login nexus-invite">
      <div className="nexus-login__ambient" aria-hidden="true">
        <div className="nexus-login__matrix">
          {Array.from({ length: 42 }).map((_, index) => (
            <span key={index} />
          ))}
        </div>
      </div>

      <Link className="nexus-login__back" to="/login">
        <ArrowLeft size={17} aria-hidden="true" />
        Back to Login
      </Link>

      <section className="nexus-login__shell nexus-invite__shell">
        <div className="nexus-login__card">
          <div className="nexus-login__brand nexus-login__brand--card">
            <span>N</span>
            <div>
              <strong>Nexus Game Engine</strong>
              <small>Membership renewal</small>
            </div>
          </div>

          <div className="nexus-login__card-head">
            <p className="nexus-login__eyebrow">Renew access</p>
            <h1>Restore workspace access</h1>
            <p className="nexus-invite__intro">
              Enter the workspace name and billing email, then continue to
              Stripe to renew the membership.
            </p>
          </div>

          {renewalStatus === "success" && (
            <div className="nexus-login__mobile-note">
              <ShieldCheck size={18} aria-hidden="true" />
              <span>
                Renewal checkout is complete. Access updates when Stripe
                confirms the subscription status.
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="nexus-login__form">
            <label className="nexus-login__field">
              <span>Workspace Name</span>
              <div>
                <Building2 size={18} aria-hidden="true" />
                <input
                  value={cardroom}
                  onChange={(event) => setCardroom(event.target.value)}
                  required
                  autoComplete="organization"
                />
              </div>
            </label>

            <label className="nexus-login__field">
              <span>Billing Email</span>
              <div>
                <Mail size={18} aria-hidden="true" />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
            </label>

            <div className="nexus-plan-select" aria-label="Membership tier">
              {tiers.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={tier === item.id ? "is-selected" : ""}
                  onClick={() => setTier(item.id)}
                >
                  {item.name}
                </button>
              ))}
            </div>

            <div className="nexus-plan-select" aria-label="Billing cycle">
              <button
                type="button"
                className={billingCycle === "monthly" ? "is-selected" : ""}
                onClick={() => setBillingCycle("monthly")}
              >
                Monthly
              </button>
              <button
                type="button"
                className={billingCycle === "annual" ? "is-selected" : ""}
                onClick={() => setBillingCycle("annual")}
              >
                Annual
              </button>
            </div>

            <div className="nexus-login__mobile-note">
              <Landmark size={18} aria-hidden="true" />
              <span>
                {allowsCard
                  ? "Basic monthly supports card or verified bank account."
                  : tier === "pro"
                    ? "Pro requires a verified US bank account and includes a base subscription plus usage billing for active members who played that month."
                    : "This plan requires a verified US bank account."}
              </span>
            </div>

            {status && <div className="nexus-login__error">{status}</div>}

            <button
              type="submit"
              disabled={loading}
              className="nexus-login__submit"
            >
              {loading
                ? "Opening Stripe..."
                : `Renew ${selectedTier.name} for $${selectedPrice}/mo${tier === "pro" ? " base" : ""}`}
              <ArrowRight size={18} aria-hidden="true" />
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
